import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Club, CreateClubRequest } from '../../types';
import { clubsApi } from '../../services/api';
import Button from '../../components/Button';
import Modal from '../../components/Modal';
import FormField, { inputStyle } from '../../components/FormField';

// ── Create Club Form ──────────────────────────────────────

interface CreateFormProps {
    onCreated: (club: Club) => void;
    onClose: () => void;
}

function CreateClubForm({ onCreated, onClose }: CreateFormProps) {
    const [form, setForm] = useState<CreateClubRequest>({
        name: '',
        slug: '',
        primaryColour: '#1a6b3c',
        timeZone: 'Australia/Perth',
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const set = (field: keyof CreateClubRequest, value: string) => {
        setForm(f => ({ ...f, [field]: value }));
        // Auto-generate slug from name
        if (field === 'name') {
            setForm(f => ({
                ...f,
                name: value,
                slug: value.toLowerCase()
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/^-|-$/g, '')
            }));
        }
    };

    const handleSubmit = async () => {
        if (!form.name.trim()) { setError('Club name is required'); return; }
        if (!form.slug.trim()) { setError('Slug is required'); return; }

        setSaving(true);
        setError('');
        try {
            const club = await clubsApi.create(form);
            onCreated(club);
        } catch (e: any) {
            setError(e.message ?? 'Failed to create club');
        } finally {
            setSaving(false);
        }
    };

    const timezones = [
        'Australia/Perth',
        'Australia/Adelaide',
        'Australia/Darwin',
        'Australia/Brisbane',
        'Australia/Sydney',
        'Australia/Melbourne',
        'Australia/Hobart',
    ];

    return (
        <>
            {error && (
                <div style={styles.errorBanner}>{error}</div>
            )}

            <FormField label="Club Name">
                <input
                    style={inputStyle}
                    value={form.name}
                    onChange={e => set('name', e.target.value)}
                    placeholder="e.g. Joondalup Resort Golf Club"
                    autoFocus
                />
            </FormField>

            <FormField
                label="URL Slug"
                hint="Used in the display URL: display.19thview.com.au/your-slug"
            >
                <input
                    style={inputStyle}
                    value={form.slug}
                    onChange={e => set('slug', e.target.value.toLowerCase()
                        .replace(/[^a-z0-9-]/g, ''))}
                    placeholder="e.g. joondalup-resort"
                />
            </FormField>

            <FormField label="Primary Colour">
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <input
                        type="color"
                        value={form.primaryColour}
                        onChange={e => set('primaryColour', e.target.value)}
                        style={{ width: 48, height: 40, border: 'none',
                                 borderRadius: 6, cursor: 'pointer' }}
                    />
                    <input
                        style={{ ...inputStyle, flex: 1 }}
                        value={form.primaryColour}
                        onChange={e => set('primaryColour', e.target.value)}
                        placeholder="#1a6b3c"
                    />
                </div>
            </FormField>

            <FormField label="Time Zone">
                <select
                    style={inputStyle}
                    value={form.timeZone}
                    onChange={e => set('timeZone', e.target.value)}
                >
                    {timezones.map(tz => (
                        <option key={tz} value={tz}>{tz}</option>
                    ))}
                </select>
            </FormField>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit} disabled={saving}>
                    {saving ? 'Creating...' : 'Create Club'}
                </Button>
            </div>
        </>
    );
}

// ── Club Card ─────────────────────────────────────────────

function ClubCard({ club, onClick }: { club: Club; onClick: () => void }) {
    const liveCameras = club.cameras?.filter(c => c.isLive).length ?? 0;
    const totalCameras = club.cameras?.length ?? 0;

    return (
        <div style={styles.card} onClick={onClick}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{
                    ...styles.clubDot,
                    background: club.primaryColour || '#1a6b3c'
                }}>
                    {club.name.charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                    <div style={styles.clubName}>{club.name}</div>
                    <div style={styles.clubSlug}>
                        display.19thview.com.au/{club.slug}
                    </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <div style={{
                        ...styles.statusBadge,
                        background: club.isActive ? '#e8f5ee' : '#f5f5f5',
                        color: club.isActive ? '#1a6b3c' : '#999',
                    }}>
                        {club.isActive ? 'Active' : 'Inactive'}
                    </div>
                </div>
            </div>

            <div style={styles.cardFooter}>
                <span style={styles.stat}>
                    📷 {totalCameras} camera{totalCameras !== 1 ? 's' : ''}
                </span>
                {liveCameras > 0 && (
                    <span style={{ ...styles.stat, color: '#ef4444' }}>
                        ● {liveCameras} live
                    </span>
                )}
                <span style={styles.stat}>
                    🏷️ {club.sponsors?.length ?? 0} sponsor{(club.sponsors?.length ?? 0) !== 1 ? 's' : ''}
                </span>
                <span style={{ ...styles.stat, marginLeft: 'auto', color: '#aaa' }}>
                    View details →
                </span>
            </div>
        </div>
    );
}

// ── Main Page ─────────────────────────────────────────────

export default function ClubsPage() {
    const navigate = useNavigate();
    const [clubs, setClubs] = useState<Club[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCreate, setShowCreate] = useState(false);

    const loadClubs = async () => {
        setLoading(true);
        setError('');
        try {
            const data = await clubsApi.getAll();
            setClubs(data);
        } catch (e: any) {
            setError(e.message ?? 'Failed to load clubs');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadClubs(); }, []);

    const handleCreated = (club: Club) => {
        setShowCreate(false);
        setClubs(prev => [...prev, club]);
    };

    return (
        <div style={styles.page}>
            {/* Header */}
            <div style={styles.header}>
                <div>
                    <h1 style={styles.title}>Clubs</h1>
                    <p style={styles.subtitle}>
                        {clubs.length} club{clubs.length !== 1 ? 's' : ''} registered
                    </p>
                </div>
                <Button onClick={() => setShowCreate(true)}>
                    + Add Club
                </Button>
            </div>

            {/* Content */}
            {loading && (
                <div style={styles.centre}>Loading clubs...</div>
            )}

            {error && (
                <div style={styles.errorBanner}>{error}</div>
            )}

            {!loading && !error && clubs.length === 0 && (
                <div style={styles.empty}>
                    <div style={styles.emptyIcon}>⛳</div>
                    <div style={styles.emptyTitle}>No clubs yet</div>
                    <div style={styles.emptyDesc}>
                        Add your first club to get started.
                    </div>
                    <div style={{ marginTop: 16 }}>
                        <Button onClick={() => setShowCreate(true)}>
                            Add your first club
                        </Button>
                    </div>
                </div>
            )}

            {!loading && clubs.length > 0 && (
                <div style={styles.list}>
                    {clubs.map(club => (
                        <ClubCard
                            key={club.id}
                            club={club}
                            onClick={() => navigate(`/admin/clubs/${club.id}`)}
                        />
                    ))}
                </div>
            )}

            {/* Create modal */}
            {showCreate && (
                <Modal
                    title="Add New Club"
                    onClose={() => setShowCreate(false)}
                >
                    <CreateClubForm
                        onCreated={handleCreated}
                        onClose={() => setShowCreate(false)}
                    />
                </Modal>
            )}
        </div>
    );
}

// ── Styles ────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
    page: {
        padding: 32, maxWidth: 900,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    },
    header: {
        display: 'flex', alignItems: 'flex-start',
        justifyContent: 'space-between', marginBottom: 28,
    },
    title: { fontSize: 26, fontWeight: 700, margin: 0, marginBottom: 4 },
    subtitle: { fontSize: 14, color: '#888', margin: 0 },
    list: { display: 'flex', flexDirection: 'column', gap: 12 },
    card: {
        background: '#fff', borderRadius: 12,
        border: '1.5px solid #eee', padding: '20px 24px',
        cursor: 'pointer', transition: 'all 0.15s',
    },
    clubDot: {
        width: 44, height: 44, borderRadius: 10,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 18, fontWeight: 700, color: '#fff', flexShrink: 0,
    },
    clubName: { fontSize: 16, fontWeight: 600, marginBottom: 3 },
    clubSlug: { fontSize: 12, color: '#888' },
    statusBadge: {
        fontSize: 12, fontWeight: 600,
        padding: '4px 10px', borderRadius: 20,
    },
    cardFooter: {
        display: 'flex', gap: 16, alignItems: 'center',
        marginTop: 16, paddingTop: 14,
        borderTop: '1px solid #f0f0f0',
    },
    stat: { fontSize: 13, color: '#666' },
    centre: {
        textAlign: 'center', color: '#888',
        padding: 48, fontSize: 14,
    },
    empty: {
        textAlign: 'center', padding: 64,
        background: '#f9f9f9', borderRadius: 12,
    },
    emptyIcon: { fontSize: 48, marginBottom: 16 },
    emptyTitle: { fontSize: 18, fontWeight: 600, marginBottom: 8 },
    emptyDesc: { fontSize: 14, color: '#888' },
    errorBanner: {
        background: '#fff0f0', color: '#c00',
        border: '1px solid #fcc', borderRadius: 8,
        padding: '12px 16px', fontSize: 14, marginBottom: 16,
    },
};